import type { FC } from 'react';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button/button';
import type { PopupProps } from '@atlaskit/popup/types';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useLoomEntryPointErrorCallback } from '@confluence/loom-utils/entry-points/useLoomEntryPointErrorCallback.ts';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant.ts';
import { LoadableAfterPaint, useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import {
	LoomCrossFlowPopupLoader,
	LoomCrossJoinPopupLoader,
} from '@confluence/loom-utils/entry-points/LoomCrossSellPopupLoaders.tsx';
import { DiscoverabilityPulse } from '@confluence/loom-utils/entry-points/DiscoverabilityPulse.ts';
import { useDiscoverabilityPulse } from '@confluence/loom-utils/entry-points/useDiscoverabilityPulse.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { expVal } from '@confluence/feature-experiments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import { LoomVideoIcon } from './VideoIcon.tsx';
import { LoomPageHeaderPlaceholder } from './LoomPageHeaderPlaceholder.tsx';
import type { Placement } from '@atlaskit/spotlight/types';

const PageHeaderLoomButtonLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PageHeaderLoomButton" */ './PageHeaderLoomButton.tsx'
			)
		).PageHeaderLoomButton,
	loading: ({ isCircle }) => <LoomPageHeaderPlaceholder isCircle={isCircle} />,
});

const LoomCrossFlowPopupCardLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LoomCrossFlowPopupCard" */ '@confluence/loom-utils/entry-points/LoomCrossFlowPopupCard.ts'
			)
		).LoomCrossFlowPopupCard,
	loading: () => <LoomPageHeaderPlaceholder />,
});

const LoomCrossJoinPopupCardLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LoomCrossJoinPopupCard" */ '@confluence/loom-utils/entry-points/LoomCrossJoinPopupCard.ts'
			)
		).LoomCrossJoinPopupCard,
	loading: () => <LoomPageHeaderPlaceholder />,
});

/**
 * This touchpoint was previously in the page header, but has now been moved to the comments sidebar.
 * The naming will remain the same to avoid breaking any existing usages of this name.
 */
const ENTRYPOINT_ID = 'loom-confluence-page-header';
const SOURCE_COMPONENT = 'loomConfluencePageHeader';
const SOURCE_CONTEXT = 'confluencePageHeaderRecordAVideo';

const i18n = defineMessages({
	crossJoinEntryPointLabel: {
		id: 'page-header-loom-button.crossJoinEntryPointLabel',
		defaultMessage: 'Join your team on Loom to record a video',
		description:
			'The a11y-friendly ARIA label for the Loom button in the page header menu when a user is eligible for cross-join',
	},
	crossSellPopupCardDescription: {
		id: 'page-header-loom-button.crossSellPopupCardDescription',
		defaultMessage:
			'Save time and engage your team with a video walkthrough, demo, update, and more.',
		description: 'Description inside a popup card advertising an opportunity to try Loom',
	},
	crossSellPopupCardTitle: {
		id: 'page-header-loom-button.crossSellPopupCardTitle',
		defaultMessage: 'Share your work with video',
		description: 'Title of a popup card advertising an opportunity to try Loom',
	},
	entryPointLabel: {
		id: 'page-header-loom-button.entryPointLabel',
		defaultMessage: 'Record a Loom video',
		description:
			'The a11y-friendly ARIA label and tooltip for the button in the page header to record a Loom video.',
	},
});

type PageHeaderLoomEntryPointComponentProps = {
	contentId: string;
	crossSellPopupPlacement?: PopupProps['placement'];
	crossSellSpotlightPlacement?: Placement;
	spaceKey: string;
	contentType: string;
	flags: FlagsStateContainer;
	isCircle?: boolean;
	testId?: string;
	source?: string;
	isDisabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const PageHeaderLoomEntryPointComponent: FC<PageHeaderLoomEntryPointComponentProps> = ({
	contentId,
	crossSellPopupPlacement,
	crossSellSpotlightPlacement,
	spaceKey,
	contentType,
	flags,
	isCircle = false,
	testId,
	source,
	isDisabled = false,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const pageMode = useGetPageMode();
	let entryPointVariant, error, loading;

	// if page is SSRd and the entry point variant was successfully preloaded during SSR
	// use the results from the window object
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if ((process.env.REACT_SSR || window.__SSR_RENDERED__) && window.__SSR_LOOM_ENTRY_POINT__) {
		entryPointVariant = window.__SSR_LOOM_ENTRY_POINT__.entryPointVariant;
		error = window.__SSR_LOOM_ENTRY_POINT__.error;
		loading = window.__SSR_LOOM_ENTRY_POINT__.loading;
	}

	// this hook will automatically return the preloaded entry point variant if it exists
	// however, this check will still occur for transitions/non-SSRd pages
	const entryPointFromState = useLoomEntryPointVariant();
	entryPointVariant = entryPointFromState.entryPointVariant;
	error = entryPointFromState.error;
	loading = entryPointFromState.loading;

	/**
	 * NONE being included here is to address a layout shift issue within the PageHeader while
	 * the entrypoint is loading. If we don't include NONE here, while a page is loading
	 * nothing will show where the entrypoint is, and then on load the entrypoint will pop in
	 * and shift the buttons next to it to the left.
	 */
	const isEntryPointEnabled = ['BETA', 'CO_USE', 'CROSS_FLOW', 'CROSS_JOIN', 'NONE'].includes(
		entryPointVariant,
	);

	const { isPulseActive } = useDiscoverabilityPulse(ENTRYPOINT_ID);

	const intl = useIntl();

	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });

	useEffect(() => {
		if (entryPointVariant === 'CROSS_JOIN') {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					action: 'viewed',
					name: 'createLoomHeaderItem',
					attributes: {
						adType: 'crossJoin',
						entrypoint: ENTRYPOINT_ID,
						sourceComponent: SOURCE_COMPONENT,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, entryPointVariant]);

	if (!isEntryPointEnabled) {
		return null;
	}

	if (entryPointVariant === 'BETA' || entryPointVariant === 'CO_USE') {
		return (
			<PageHeaderLoomButtonLoader
				contentId={contentId}
				contentType={contentType}
				dataVC="loom-button"
				label={intl.formatMessage(i18n.entryPointLabel)}
				spaceKey={spaceKey}
				ssrPlaceholderIdProp={ssrPlaceholderIdProp}
				isCircle={isCircle}
				testId={testId}
				source={source}
				isDisabled={isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)}
			/>
		);
	}

	if (error) {
		return (
			<Button
				appearance="subtle"
				aria-label={intl.formatMessage(i18n.entryPointLabel)}
				data-vc="loom-button"
				iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
				onClick={loomEntryPointErrorCallback}
			/>
		);
	}

	if (loading) {
		return <LoomPageHeaderPlaceholder />;
	}

	if (entryPointVariant === 'CROSS_FLOW') {
		if (
			pageMode === PageMode.VIEW &&
			expVal('confluence_selection_hydration_loom_button', 'isEnabled', false)
		) {
			return (
				<LoomCrossFlowPopupLoader
					description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
					entryPointId={ENTRYPOINT_ID}
					spotlightPlacement={crossSellSpotlightPlacement}
					sourceActionSubjectId="createLoomHeaderItem"
					sourceComponent={SOURCE_COMPONENT}
					sourceContext={SOURCE_CONTEXT}
					title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
					isCircle={isCircle}
					ariaLabel={intl.formatMessage(i18n.entryPointLabel)}
					iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
					appearance="subtle"
					immediateActivationCondition={isPulseActive}
				/>
			);
		}
		return (
			<LoomCrossFlowPopupCardLoader
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				entryPointId={ENTRYPOINT_ID}
				spotlightPlacement={crossSellSpotlightPlacement}
				sourceActionSubjectId="createLoomHeaderItem"
				sourceComponent={SOURCE_COMPONENT}
				sourceContext={SOURCE_CONTEXT}
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				trigger={(triggerProps) => (
					<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID} isCircle={isCircle}>
						<ButtonSpacer>
							<Button
								{...triggerProps}
								appearance="subtle"
								aria-label={intl.formatMessage(i18n.entryPointLabel)}
								data-vc="loom-button"
								iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
								{...ssrPlaceholderIdProp}
							/>
						</ButtonSpacer>
					</DiscoverabilityPulse>
				)}
			/>
		);
	}

	if (entryPointVariant === 'CROSS_JOIN') {
		if (
			pageMode === PageMode.VIEW &&
			expVal('confluence_selection_hydration_loom_button', 'isEnabled', false)
		) {
			return (
				<LoomCrossJoinPopupLoader
					description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
					placement={crossSellPopupPlacement}
					sourceActionSubjectId="createLoomHeaderItem"
					title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
					entryPointId={ENTRYPOINT_ID}
					isCircle={isCircle}
					ariaLabel={intl.formatMessage(i18n.crossJoinEntryPointLabel)}
					iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
					appearance="subtle"
					immediateActivationCondition={isPulseActive}
					flags={flags}
					sourceComponent={SOURCE_COMPONENT}
				/>
			);
		}
		return (
			<LoomCrossJoinPopupCardLoader
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				placement={crossSellPopupPlacement}
				sourceActionSubjectId="createLoomHeaderItem"
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				entryPointId={ENTRYPOINT_ID}
				triggerLink={(href, { onClick, ...otherTriggerProps }) => (
					<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID} isCircle={isCircle}>
						<ButtonSpacer>
							<Button
								{...otherTriggerProps}
								appearance="subtle"
								aria-label={intl.formatMessage(i18n.crossJoinEntryPointLabel)}
								data-vc="loom-button"
								href={href}
								iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
								rel="noreferrer"
								target="_blank"
								onClick={onClick}
								{...ssrPlaceholderIdProp}
							/>
						</ButtonSpacer>
					</DiscoverabilityPulse>
				)}
				flags={flags}
				sourceComponent={SOURCE_COMPONENT}
			/>
		);
	}

	// This condition currently isn't possible, but explicitly handling it here in case the
	// logic defining `isEntryPointEnabled` changes without updating this rendering behavior
	return null;
};

export const PageHeaderLoomEntryPoint: FC<Omit<PageHeaderLoomEntryPointComponentProps, 'flags'>> =
	withErrorBoundary({
		attribution: Attribution.CONTENT_TYPES,
	})(withFlags(PageHeaderLoomEntryPointComponent));
